export interface FeatureProps {
    title: string;
    eventName: string;
    icon: string;
    summary: string;
    url: string | null;
    description: string | null;
    isPro?: boolean;
}

export class Feature {
    private static readonly CLOUDFLARE_ACCOUNT = "bn5mkaqpuy7b28a7";

    public title: string;
    public description: string;
    public icon: string;
    public videoUrl: string | null;
    public eventName: string;
    public fullDescription: string | null;
    public isPro: boolean;

    constructor(props: FeatureProps) {
        this.title = props.title;
        this.description = props.summary;
        this.icon = `Subscribe/${props.icon}.png`;
        this.videoUrl = props.url;
        this.eventName = props.eventName;
        this.fullDescription = props.description;
        this.isPro = props.isPro || false;
    }

    // Feature instances
    public static readonly KNOWLEDGE_MAP = new Feature({
        title: "Knowledge map",
        eventName: "knowledge-map",
        icon: "Knowledge-Map",
        summary: "View the full list of words you know and need to learn.",
        url: "",
        description: null
    });

    public static readonly CONTEXT = new Feature({
        title: "Learn in context",
        eventName: "learn-context",
        icon: "Learn-in-Context",
        summary: "Get real-life examples of words through audio-visual learning.",
        url: "",
        description: null
    });

    public static readonly SMART_REPETITION = new Feature({
        title: "Smart repetition",
        eventName: "smart-repeat",
        icon: "Smart-Repetition",
        summary: "Review words at regular intervals and don't forget them.",
        url: "",
        description: null
    });

    public static readonly PROGRESS_TRACKING = new Feature({
        title: "Progress tracking",
        eventName: "progress-track",
        icon: "Progress-Tracking",
        summary: "Monitor your learning progress on a daily, weekly, or monthly basis.",
        url: "",
        description: null
    });

    public static readonly NO_ADS = new Feature({
        title: "No ads",
        eventName: "no-ads",
        icon: "No-Ads",
        summary: "Learn with no interruptions or annoying ads.",
        url: Feature.getCloudflareUrl("0bb235076c7bf227503e8a4793386b2a"),
        description: null,
        isPro: true
    });

    public static readonly UNLIMITED_HEARTS = new Feature({
        title: "Unlimited hearts",
        eventName: "hearts",
        icon: "unlimited-hearts",
        summary: "Learn at your own pace and never run out of hearts.",
        url: null,
        description: "Work through your daily reviews faster without worrying about making mistakes.",
        isPro: true
    });

    public static readonly AI_CHAT = new Feature({
        title: "Unlimited AI Chat",
        eventName: "chat",
        icon: "AI-Chat_B",
        summary: "Join the future of learning with Lexi as a practice buddy/tutor.",
        url: Feature.getCloudflareUrl("738789c8f150b2fa163ad0f04a3ce93b"),
        description: null,
        isPro: true
    });

    public static readonly TRANSLATIONS = new Feature({
        title: "Translations",
        eventName: "translation",
        icon: "Translation",
        summary: "Everything you need, translated to your native language.",
        url: null,
        description: "Confidently understand every example, pro tip, or even AI chat message.",
        isPro: true
    });

    public static readonly PHOTO_VIDEO = new Feature({
        title: "Word photos & videos",
        eventName: "wordvideo",
        icon: "Word-photos-and-videos",
        summary: "Over 500,000 clips to bring every word to life, because your memory is visual.",
        url: Feature.getCloudflareUrl("2617d5576ebfb63a241a7a6462e79f82"),
        description: null,
        isPro: true
    });

    public static readonly IDIOMS = new Feature({
        title: "Idioms & phrases",
        eventName: "idioms",
        icon: "Idioms_feature",
        summary: "Speak English like a native, with over 8,000 everyday expressions.",
        url: Feature.getCloudflareUrl("536389033435990edfbaa71827072345"),
        description: null,
        isPro: true
    });

    public static readonly WRITING_FEEDBACK = new Feature({
        title: "Writing feedback",
        eventName: "writing",
        icon: "Writing",
        summary: "Improve your English writing with real-time AI feedback tips.",
        url: null,
        description: "Advance your grammar and vocabulary usage with smart personalised AI tips!",
        isPro: true
    });

    public static readonly EXAMS = new Feature({
        title: "IELTS & TOEFL Prep",
        eventName: "exams",
        icon: "Exam-Prep_B",
        summary: "Get a higher score by focusing on exam-specific word lists.",
        url: Feature.getCloudflareUrl("a9474b2e2a6717748274cbf0a886bb7d"),
        description: null,
        isPro: true
    });

    public static readonly TOPICS = new Feature({
        title: "Topics & Word-lists",
        eventName: "topics",
        icon: "Topics_B",
        summary: "Focus on specific topics you need to master today.",
        url: Feature.getCloudflareUrl("484f12ad10f6306d056b2fb9edc707da"),
        description: null,
        isPro: true
    });

    public static readonly CHROME = new Feature({
        title: "Chrome Extension",
        eventName: "chrome",
        icon: "ChromeExtension",
        summary: "Add words to your learning list while reading online.",
        url: Feature.getCloudflareUrl("74913457a4baf526a7dcb8223b8a3d25"),
        description: null,
        isPro: true
    });

    public static readonly PRO_TIPS = new Feature({
        title: "Pro Tips",
        eventName: "pro-tips",
        icon: "pro-tips",
        summary: "Master every word's correct application for total English confidence.",
        url: Feature.getCloudflareUrl("10139d5c79de3477355c51c5cba28557"),
        description: null,
        isPro: true
    });

    public static readonly CUSTOM_LISTS = new Feature({
        title: "Custom Word Lists",
        eventName: "custom-lists",
        icon: "custom-lists",
        summary: "Create your custom word list and organize words the way you like.",
        url: Feature.getCloudflareUrl("57a178e3cac3153fccc93172821d283a"),
        description: null,
        isPro: true
    });

    public static readonly LESSONS = new Feature({
        title: "Lessons",
        eventName: "lesson",
        icon: "lessons",
        summary: "Practice Speaking, Listening and writing while asking real questions?",
        url: Feature.getCloudflareUrl("73bdbe294a87b40890c8430efabc5c58"),
        description: null,
        isPro: true
    });

    // Static features arrays
    public static readonly FREE_FEATURES = [
        Feature.KNOWLEDGE_MAP,
        Feature.CONTEXT,
        Feature.SMART_REPETITION,
        Feature.PROGRESS_TRACKING
    ];

    public static readonly PRO_FEATURES = [
        Feature.PRO_TIPS,
        Feature.PHOTO_VIDEO,
        Feature.AI_CHAT,
        Feature.CUSTOM_LISTS,
        Feature.LESSONS,
        Feature.EXAMS,
        Feature.TOPICS,
        Feature.TRANSLATIONS,
        Feature.WRITING_FEEDBACK,
        Feature.IDIOMS,
        Feature.CHROME,
        Feature.UNLIMITED_HEARTS,
        Feature.NO_ADS
    ];

    private static getCloudflareDownloadLink(id: string): string {
        return `https://customer-${Feature.CLOUDFLARE_ACCOUNT}.cloudflarestream.com/${id}/downloads/default.mp4`;
    }

    protected static getCloudflareUrl(id: string): string {
        return `https://customer-${Feature.CLOUDFLARE_ACCOUNT}.cloudflarestream.com/${id}/iframe?autoplay=true&defaultTextTrack=en`;
    }

    public static getSecondVideo(languageCode: string): string {
        const videoMap: { [key: string]: string } = {
            'de': 'a08929d054e26d93e3ab11dc90a0603c',
            'tr': '015db05017286181dfc58705533faed3',
            'ar': '6d2b4f17bb173cdee5a284be608ca238',
            'ja': '9cf6f79515634ac0af36edf7b0cd6581',
            'hi': 'e497618f2bbd48c1d8e8ad84149cc607',
            'pt': '70ed80e5c462f967b6fcb476ace930a8',
            'en': '47a53e8ea79ed2d07475a879e18819f1',
            'es': '71ec012e8ada43bb93fc7f059dd66e13',
            'fa': 'e8832061811db40d27ab3e7265bd812e'
        };

        const videoId = videoMap[languageCode] || '47a53e8ea79ed2d07475a879e18819f1';
        return Feature.getCloudflareDownloadLink(videoId);
    }

    public static forSubject(subject: any): Feature {
        if (subject.isExam()) return Feature.EXAMS;
        if (subject.isPhrases()) return Feature.IDIOMS;
        return Feature.TOPICS;
    }

    public async buy(): Promise<void> {
        // Implement the buy functionality
        // This would need to be adapted to your React application's navigation/routing system
    }

    private getPromoTitle(): string {
        // Implement the promo title logic
        return this.title;
    }

    public async peek(): Promise<void> {
        // Implement the peek functionality
        // This would need to be adapted to your React application's dialog system
    }
}

// VideoGuidesFeature class
export interface VideoGuidesFeatureProps extends FeatureProps {
    priorIcon: string;
    onTapped: () => Promise<void>;
}

export class VideoGuidesFeature extends Feature {
    public priorIcon: string;
    public onTapped: () => Promise<void>;

    constructor(props: VideoGuidesFeatureProps) {
        super(props);
        this.priorIcon = props.priorIcon;
        this.onTapped = props.onTapped;
    }

    public static readonly DISCOVER_WORDS = new VideoGuidesFeature({
        title: "Discover",
        eventName: "",
        icon: "brain-focus",
        summary: "Learn how to discover new words and add them to your knowledge map.",
        url: Feature.getCloudflareUrl("2a4a60c9daa704c8ad1264ca7cf1400a"),
        description: null,
        priorIcon: "brain-focus",
        onTapped: async () => {
            // Implement navigation to discover select page
        }
    });

    public static readonly PRACTICE_WORDS = new VideoGuidesFeature({
        title: "Practice",
        eventName: "",
        icon: "copy-focus",
        summary: "Learn how to review the new words you have discovered.",
        url: Feature.getCloudflareUrl("fd41ebc50b2128543f3f048f8d0064ca"),
        description: null,
        priorIcon: "copy-focus",
        onTapped: async () => {
            // Implement navigation to practice page
        }
    });

    public static readonly LESSONS_TUTORIAL = new VideoGuidesFeature({
        title: "Lessons",
        eventName: "",
        icon: "friends-focus",
        summary: "Practice speaking, listening, and writing.",
        url: Feature.getCloudflareUrl("63891699a75c267cc4cdf6bcbd781c7a"),
        description: null,
        priorIcon: "friends-focus",
        onTapped: async () => {
            // Implement navigation to scenarios map page
        }
    });

    public static readonly VIDEO_GUIDES = [
        VideoGuidesFeature.DISCOVER_WORDS,
        VideoGuidesFeature.PRACTICE_WORDS,
        VideoGuidesFeature.LESSONS_TUTORIAL
    ];
}
